/**
 * LatestUsersListItem
 *
 * A single row in the latest-users list: avatar + display name (with the
 * "You"/"No name" rules) + optional platform-name suffix + "Created"/"Last seen"
 * timestamp lines. The whole row is tappable and navigates to the user's
 * details screen.
 *
 * Ports Flutter `lib/presentation/screens/user/widgets/latest_users_list_item.dart`.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { UserUtils } from '@/domain/utils/userUtils';
import { getAuthFirebaseUserUseCase } from '@/domain/usecases/user/getAuthFirebaseUserUseCase';
import { getTargetPlatformName } from '@/domain/enums/platform/targetPlatform';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { Navigator } from '@/presentation/routing/Navigator';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { UserEntity } from '@/domain/entities/user/userEntity';

export interface LatestUsersListItemProps {
  user: UserEntity;
}

export const LatestUsersListItem: React.FC<LatestUsersListItemProps> = ({ user }) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const { formatDate } = useDateFormatters();

  // Username resolution mirrors Flutter (latest_users_list_item.dart:20-25):
  // "You" for the current user, else the non-empty name, else "No name".
  const currentUid = getAuthFirebaseUserUseCase.execute()?.uid;
  const username =
    user.firebaseId === currentUid
      ? l10n.latestUsers.you
      : user.name
        ? user.name
        : l10n.latestUsers.noName;

  // Platform suffix only when the resolver returns a name (it encapsulates the
  // Flutter guard `platform != null && platform != -1`).
  const platformName = getTargetPlatformName(user.platform);

  return (
    <div
      {...qaAttr({ id: `latest-user-${user.firebaseId}` })}
      onClick={() => Navigator.navigateToUserDetails(user.firebaseId)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        cursor: 'pointer',
        paddingBottom: responsive.spacing.lg,
      }}
    >
      <CachedImage
        src={UserUtils.generateUserProfileImageUrl(user.firebaseId)}
        alt={username}
        style={{
          width: responsive.avatar.medium,
          height: responsive.avatar.medium,
          borderRadius: responsive.borderRadius.round,
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />

      <div style={{ flex: 1, marginLeft: responsive.spacing.base }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontWeight: responsive.fontWeight.semibold,
              color: theme.text.primary,
              fontSize: responsive.font.base,
            }}
          >
            {username}
          </span>
          {platformName && (
            <span
              style={{
                fontWeight: responsive.fontWeight.semibold,
                color: theme.text.primary,
                fontSize: responsive.font.base,
              }}
            >
              {` - ${platformName}`}
            </span>
          )}
        </div>

        <div style={{ marginTop: responsive.spacing.xs }}>
          <p
            style={{
              fontSize: responsive.font.sm,
              color: theme.text.secondary,
              margin: 0,
            }}
          >
            {`${l10n.latestUsers.created}: ${formatDate(user.createdTimestamp, true)}`}
          </p>
          <p
            style={{
              fontSize: responsive.font.sm,
              color: theme.text.secondary,
              margin: 0,
            }}
          >
            {`${l10n.latestUsers.lastSeen}: ${formatDate(user.lastSeenTimestamp ?? 0, true)}`}
          </p>
        </div>
      </div>
    </div>
  );
};
